import { Screen } from '@rahtab/stack-navigator';
import {
  Button,
  Card,
  CardContainer,
  Colors,
  DatePicker,
  getSpacing,
  handleHelperText,
  HorizontalLine,
  mobileValidation,
  nationalIdValidation,
  Picker,
  StepProgressBar,
  Text,
  TextInput,
  TextView,
  useAlert,
} from '@rahtab/ui';
import { RouteProp } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import React, { useState, VFC } from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import * as yup from 'yup';
import { RootStackType } from '../../Router';
import ProviderImage from './ProviderImage';
import { stepTitles } from './strings';
import { useEdit } from './useEdit';

const schema = yup.object().shape({
  nationalCode: nationalIdValidation,
  mobile: mobileValidation,
  email: yup.string().email('ایمیل وارد شده معتبر نیست.'),
});
const DATE_FORMAT = 'YYYY/MM/DD';
const DISPLAY_DATE_FORMAT = 'jYYYY/jMM/jDD';

const completeInfoTitle = 'تکمیل اطلاعات کاربری';
const editInfoTitle = 'ویرایش اطلاعات سازمانی';

const EditInfo: VFC<EditInfoProps> = ({ route }) => {
  const alert = useAlert();
  const [errors, setErrors] = useState<yup.ValidationError | null>(null);
  const { edit, editProviderInfo, screenProps, setImage } = useEdit(route.name === 'EditInfo' ? 'goBack' : 'location', {
    skipProvinceRequest: true,
  });

  const validate = () => {
    schema
      .validate(
        {
          nationalCode: screenProps.data.identificationNumber,
          mobile: screenProps.data.mobileNumber,
          email: screenProps.data.email,
        },
        { abortEarly: false },
      )
      .then(() => {
        setErrors(null);
        edit();
      })
      .catch((err) => {
        setErrors(err);
      });
  };

  const handleSelectImage = async () => {
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        alert({
          message: 'دسترسی برنامه به گالری فراهم نیست.',
        });
      } else {
        let result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          base64: true,
          allowsEditing: true,
          aspect: [4, 4],
          quality: 1,
        });
        if (!result.cancelled) {
          setImage(result);
        }
      }
    }
  };

  const disableFieldsInEditInfo = route.name !== 'EditInfo';

  const handleDisableSubmitButton = () => {
    if (route.name === 'CompleteInfo') {
      if (
        !(
          screenProps.data.landlinePhoneNumber &&
          screenProps.data.firstName &&
          screenProps.data.lastName &&
          screenProps.data.identificationNumber &&
          screenProps.data.mobileNumber
        )
      ) {
        return true;
      }
      return false;
    } else {
      if (!(screenProps.data.landlinePhoneNumber && screenProps.data.identificationNumber)) {
        return true;
      }
      return false;
    }
  };

  return (
    <Screen
      {...screenProps}
      title={route.name === 'CompleteInfo' ? completeInfoTitle : editInfoTitle}
      renderMain={({
        guilds: guildsData,
        email,
        firstName,
        guild,
        guildNumber,
        identificationNumber,
        landlinePhoneNumber,
        lastName,
        mobileNumber,
        birthDate,
        name,
        image,
      }) => {
        return (
          <ScrollView keyboardShouldPersistTaps="handled">
            <View style={{ margin: getSpacing() }}>
              <KeyboardAvoidingView>
                <Card>
                  {disableFieldsInEditInfo && (
                    <View
                      style={{
                        backgroundColor: Colors.lightBlue,
                        paddingVertical: getSpacing(),
                      }}>
                      <StepProgressBar currentStep={1} stepsTitle={stepTitles} />
                    </View>
                  )}
                  <CardContainer>
                    <ProviderImage
                      imageUri={!image.cancelled ? image.uri : undefined}
                      onPress={() => {
                        handleSelectImage();
                      }}
                    />
                    <TouchableOpacity
                      onPress={() => {
                        handleSelectImage();
                      }}>
                      <Text style={{ alignSelf: 'center' }} color="primary" variant="mediumBody">
                        ویرایش تصویر نشان فروشگاه
                      </Text>
                    </TouchableOpacity>
                    <HorizontalLine />
                    <Text variant="cardTitle">اطلاعات حساب کاربری خود را با دقت وارد نمایید.</Text>

                    <TextView title="نام پذیرنده" value={name} />
                    <Picker
                      disabled={route.name === 'EditInfo'}
                      selectedValue={guild}
                      onSelectValue={(val) => editProviderInfo('guild', val)}
                      label="صنف"
                      placeholder="صنف مربوطه را انتخاب نمایید.">
                      {guildsData.map((g, index) => {
                        return <Picker.Item key={`guild-${index}`} {...g} />;
                      })}
                    </Picker>
                    {guild !== '0' && (
                      <TextInput
                        editable={disableFieldsInEditInfo}
                        value={guildNumber}
                        label="شناسه واحد صنفی"
                        placeholder="شناسه واحد صنفی پذیرنده را وارد نمایید."
                        onChangeText={(text) => editProviderInfo('guildNumber', text)}
                      />
                    )}
                    <TextInput
                      editable={disableFieldsInEditInfo}
                      value={firstName}
                      onChangeText={(text) => editProviderInfo('firstName', text)}
                      placeholder="نام نماینده پذیرنده را وارد نمایید."
                      label="نام نماینده"
                    />
                    <TextInput
                      editable={disableFieldsInEditInfo}
                      value={lastName}
                      onChangeText={(text) => editProviderInfo('lastName', text)}
                      placeholder="نام خانوادگی نماینده پذیرنده را وارد نمایید."
                      label="نام خانوادگی نماینده"
                    />
                    <TextInput
                      value={landlinePhoneNumber}
                      onChangeText={(text) => editProviderInfo('landlinePhoneNumber', text)}
                      placeholder="شماره تلفن ثابت خود را وارد نمایید."
                      label="شماره تلفن"
                    />
                    <TextInput
                      helperText={handleHelperText('nationalCode', errors)}
                      editable={disableFieldsInEditInfo}
                      value={identificationNumber}
                      onChangeText={(text) => editProviderInfo('identificationNumber', text)}
                      placeholder="کدملی نماینده پذیرنده را وارد نمایید."
                      label="کدملی نماینده"
                    />
                    <DatePicker
                      placeholder="تاریخ تولد نماینده پذیرنده را وارد نمایید."
                      label="تاریخ تولد نماینده"
                      value={birthDate}
                      onConfirm={(text) => editProviderInfo('birthDate', text)}
                      disabled={!disableFieldsInEditInfo}
                      format={DATE_FORMAT}
                      displayFormat={DISPLAY_DATE_FORMAT}
                    />
                    <TextInput
                      value={mobileNumber}
                      helperText={handleHelperText('mobile', errors)}
                      onChangeText={(text) => editProviderInfo('mobileNumber', text)}
                      placeholder="شماره همراه پذیرنده را وارد نمایید."
                      label="شماره همراه"
                    />
                    <TextInput
                      value={email}
                      helperText={handleHelperText('email', errors)}
                      onChangeText={(text) => editProviderInfo('email', text)}
                      placeholder="آدرس ایمیل پذیرنده را وارد نمایید."
                      label="آدرس ایمیل"
                    />
                    <Button
                      accessibilityLabel="press to edit"
                      disabled={handleDisableSubmitButton()}
                      onPress={() => {
                        validate();
                      }}
                      color="primary"
                      style={styles.button}
                      text={route.name === 'CompleteInfo' ? 'تکمیل اطلاعات سازمانی' : 'ویرایش اطلاعات سازمانی'}
                    />
                  </CardContainer>
                </Card>
              </KeyboardAvoidingView>
            </View>
          </ScrollView>
        );
      }}
    />
  );
};

export default EditInfo;

const styles = StyleSheet.create({
  map: {
    height: 300,
  },
  button: {
    marginTop: getSpacing(),
  },
});

interface EditInfoProps {
  route: RouteProp<RootStackType, 'CompleteInfo' | 'EditInfo'>;
}
